import { getTimezoneOffset, formatInTimeZone } from "date-fns-tz";
import { enCA } from "date-fns/locale";
import { isValid } from "date-fns";
import {
    DEFAULT_TIMEZONE,
    BYLINE_DATE_FORMAT,
    CANADA_TIME_ZONES,
    ONE_HOUR_IN_MS,
} from "./constants";

/**
 * Takes a duration in seconds and converts it to human readable time stamp.
 *
 * @param {Number} duration duration in seconds.
 *
 * @returns {String} transformed duration.
 */
export function getFormattedDuration(duration) {
    if (duration === undefined) return;

    const parsedDuration = Math.floor(Math.abs(parseFloat(duration)));
    if (Number.isNaN(parsedDuration)) return "";

    const hours = Math.floor(parsedDuration / 3600);
    const minutes = Math.floor((parsedDuration / 60) % 60);
    const seconds = Math.round(parsedDuration % 60);

    let result = duration >= 0 ? "" : "-";
    if (hours) {
        result += hours + ":";
        result += (minutes < 10 ? "0" + minutes : minutes) + ":";
    } else {
        result += minutes + ":";
    }
    result += seconds < 10 ? "0" + seconds : seconds;
    return result;
}

export function getTimeSince(timestamp, includeSeconds, isLive) {
    const today = Date.now();

    if (typeof timestamp === "string") timestamp = parseInt(timestamp);

    if (Number.isNaN(timestamp) || !Number.isFinite(timestamp)) {
        timestamp = today;
    }
    const str = (time, unit) => `${time} ${unit} ago`;

    const pastTemplate = (time, unit) => {
        return isLive ? `Started ` + str(time, unit) : str(time, unit);
    };
    const futureTemplate = (time, unit) => {
        return isLive ? `In ${time} ${unit}` : `${time} ${unit}`;
    };

    const nowTemplate = "now";

    const isInFuture = today < timestamp;

    const timeDiff = Math.abs(today - timestamp) / 1000;

    let timeUnits = [
        // this array is sorted by size!
        { label: "year", size: 31536000 },
        { label: "month", size: 2592000 },
        { label: "day", size: 86400 },
        { label: "hour", size: 3600 },
        { label: "minute", size: 60 },
    ];

    if (includeSeconds) {
        timeUnits.push({ label: "second", size: 1 });
    }
    let selectedTimeUnit;

    function selectNextTimeUnit() {
        selectedTimeUnit = timeUnits[timeUnits.indexOf(selectedTimeUnit) + 1];
    }
    function timeIsTooBroad() {
        return timeDiff < selectedTimeUnit.size;
    }

    do {
        selectNextTimeUnit();
        if (selectedTimeUnit === undefined) return nowTemplate;
    } while (timeIsTooBroad());

    const numTimeUnits = Math.round(timeDiff / selectedTimeUnit.size);
    const label = selectedTimeUnit.label + (numTimeUnits > 1 ? "s" : "");

    return isInFuture
        ? futureTemplate(numTimeUnits, label)
        : pastTemplate(numTimeUnits, label);
}

/**
 * Get current time in ms
 * only useful until jest modern timers stop breaking flushAllPromies
 */
export function epochMs() {
    return Date.now();
}

/**
 * Gets user's current timezone in IANA format. If the timezone is located outside of Canada, use default timezone.
 *
 * @returns {string} - User's current timezone in IANA format (E.g. "America/Edmonton")
 */
export const getUserTimeZone = () => {
    // Gets the difference in hours between the time on the local computer and Universal Coordinated Time (UTC) at the given date.
    const localTzOffsetInHours = (d) => Math.abs(d.getTimezoneOffset()) / 60; // Convert minutes -> hours

    // Get the users current timezone offsets in daylight & standard time
    const standardDate = new Date();
    standardDate.setMonth(0, 1); // January 1st
    const standardOffset = localTzOffsetInHours(standardDate); // Standard time offset (Winter)

    const daylightDate = new Date();
    daylightDate.setMonth(6, 1); // July 1st
    const daylightOffset = localTzOffsetInHours(daylightDate); // Daylight savings time offset (Summer)

    // Does timezone change time during daylight savings?
    const currentTzHasDST = standardOffset !== daylightOffset;

    // Find Canadian timezone with same timezone offset
    for (const [tzName, tzData] of Object.entries(CANADA_TIME_ZONES)) {
        const tzHasDST = !!tzData?.daylight;
        // If selected timezone doesn't have the same DST change as the users current timezone skip it
        if (tzHasDST !== currentTzHasDST) continue;
        // If timezone has the same standard offset, return the timezone
        if (standardOffset === tzData.standard.offset) {
            return tzName;
        }
    }
    // If no timezone is found, return the default timezone
    return DEFAULT_TIMEZONE;
};

/**
 * Get the locale for the given Canadian timezone and date
 *
 * @param {string} timezone - Timezone in IANA format (E.g. "America/Edmonton")
 * @param {date} date - Date to determine if in daylight or standard time
 * @returns {string} - Timezone locale (E.g. "EST", "MDT")
 */
export const getTimezoneLocale = (timezone, date = new Date()) => {
    if (!CANADA_TIME_ZONES[timezone] || !isValid(date)) return "";
    // date-fns changes to daylight savings at 2:00am UTC, not 2:00am local time so adjust the time so that the
    // 2:00am time change occurs at 2:00am local time
    const tzAdjustedDate = new Date(
        date.getTime() + getTimezoneOffset(timezone)
    );

    // Gets the difference in hours between the given timezone and Universal Coordinated Time (UTC) at the given date.
    const offsetInMilliseconds = getTimezoneOffset(timezone, tzAdjustedDate);
    const currentOffset = Math.abs(offsetInMilliseconds) / ONE_HOUR_IN_MS; // Convert milliseconds -> hours

    const timezoneObj = CANADA_TIME_ZONES[timezone];
    const standardOffset = timezoneObj?.standard?.offset;

    const isInStandardTime = currentOffset === standardOffset;

    const locale = isInStandardTime
        ? timezoneObj.standard.locale
        : timezoneObj?.daylight?.locale;

    return locale;
};

/**
 * Converts unix timestamp to readable date in the given IANA timezone for story byline. The
 * default timezone is used if the given timezone is invalid or located outside of Canada.
 *
 * @param {number} unixTimestamp - UNIX format timestamp
 * @param {string} timezone - IANA format timezone string (E.g. "America/Edmonton")
 * @returns {string} Readable date in the given timezone for story byline
 */
export const unixToZonedBylineDate = (
    unixTimestamp,
    timezone = DEFAULT_TIMEZONE
) => {
    unixTimestamp = parseInt(unixTimestamp);
    if (!unixTimestamp || !isValid(unixTimestamp)) return "";

    // If no matching timezone is found in Canadian timezone list, use default timezone
    timezone = CANADA_TIME_ZONES[timezone] ? timezone : DEFAULT_TIMEZONE;

    const date = new Date(unixTimestamp);
    const locale = getTimezoneLocale(timezone, date); // NST/NDT is not included in date-fns so locale must be calculated manually

    const finalDate = formatInTimeZone(date, timezone, BYLINE_DATE_FORMAT, {
        locale: enCA,
    });
    return `${finalDate} ${locale}`;
};
